import { useState, useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { digitalTwinApi } from '../../../../Restclient/datacollection';

// Module-level cache: dashboardId → normalized dashboard object.
// Survives component unmount/remount so re-opening the same component is instant.
// The cache is invalidated on every successful save so stale data is never shown.
const dashboardCache = new Map();

/**
 * Pre-warms the dashboard cache for a list of IDs.
 * Called by useComponentBindings once the binding document has loaded so that
 * every dashboard linked to the current model is already in memory before the
 * user clicks any component.  IDs already in cache are skipped.
 */
export async function primeDashboardCache(dashboardIds) {
  const toFetch = (dashboardIds || []).filter((id) => id && !dashboardCache.has(id));
  if (toFetch.length === 0) return;
  await Promise.all(
    toFetch.map(async (id) => {
      try {
        const res = await digitalTwinApi.getDashboard(id);
        dashboardCache.set(id, normalizeDashboard(res.data));
      } catch (_) {}
    })
  );
}

// Migrate dashboards that used the old flat `widgets` array to the new per-tab structure.
function normalizeDashboard(data) {
  if (!data) return null;
  if (!data.tabs || data.tabs.length === 0) {
    return {
      ...data,
      tabs: [{ id: uuidv4(), label: 'Overview', widgets: data.widgets || [] }],
    };
  }
  return data;
}

function serializeTabs(tabs) {
  return JSON.stringify(tabs);
}

/**
 * @param {string|null} dashboardId  - The actual CosmosDB document id of the dashboard (used for fetch/update).
 */
export function useDTDashboard(dashboardId) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const baselineRef = useRef(null);

  const fetchDashboard = useCallback(async (forceRefresh = false) => {
    if (!dashboardId) return;

    // Serve from cache on first open — avoids a round-trip to CosmosDB
    if (!forceRefresh && dashboardCache.has(dashboardId)) {
      const cached = dashboardCache.get(dashboardId);
      setDashboard(cached);
      baselineRef.current = serializeTabs(cached.tabs);
      setIsDirty(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      const res = await digitalTwinApi.getDashboard(dashboardId);
      const normalized = normalizeDashboard(res.data);
      dashboardCache.set(dashboardId, normalized);
      setDashboard(normalized);
      baselineRef.current = serializeTabs(normalized.tabs);
      setIsDirty(false);
      setError(null);
    } catch (err) {
      if (err.response?.status === 404) {
        dashboardCache.delete(dashboardId);
        setDashboard(null);
      } else {
        setError(err.message || 'Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [dashboardId]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // ── Per-tab widget management ────────────────────────────────────────────

  const updateTabWidgets = useCallback((tabId, widgets) => {
    setDashboard((prev) => {
      if (!prev) return prev;
      const updatedTabs = prev.tabs.map((t) => (t.id === tabId ? { ...t, widgets } : t));
      setIsDirty(serializeTabs(updatedTabs) !== baselineRef.current);
      return { ...prev, tabs: updatedTabs };
    });
  }, []);

  const addTab = useCallback((label) => {
    setDashboard((prev) => {
      if (!prev) return prev;
      const updatedTabs = [...prev.tabs, { id: uuidv4(), label, widgets: [] }];
      setIsDirty(serializeTabs(updatedTabs) !== baselineRef.current);
      return { ...prev, tabs: updatedTabs };
    });
  }, []);

  const removeTab = useCallback((tabId) => {
    setDashboard((prev) => {
      if (!prev || prev.tabs.length <= 1) return prev;
      const updatedTabs = prev.tabs.filter((t) => t.id !== tabId);
      setIsDirty(serializeTabs(updatedTabs) !== baselineRef.current);
      return { ...prev, tabs: updatedTabs };
    });
  }, []);

  const renameTab = useCallback((tabId, label) => {
    setDashboard((prev) => {
      if (!prev) return prev;
      const updatedTabs = prev.tabs.map((t) => (t.id === tabId ? { ...t, label } : t));
      setIsDirty(serializeTabs(updatedTabs) !== baselineRef.current);
      return { ...prev, tabs: updatedTabs };
    });
  }, []);

  const updateName = useCallback((name) => {
    setDashboard((prev) => (prev ? { ...prev, name } : prev));
    setIsDirty(true);
  }, []);

  // ── Persistence ──────────────────────────────────────────────────────────

  const saveDashboard = useCallback(async () => {
    if (!dashboard) return;
    try {
      setSaving(true);
      const payload = { name: dashboard.name, tabs: dashboard.tabs };
      let saved;
      if (dashboard.id) {
        const res = await digitalTwinApi.updateDashboard(dashboard.id, payload);
        saved = normalizeDashboard(res.data);
      } else {
        const res = await digitalTwinApi.createDashboard(payload);
        saved = normalizeDashboard(res.data);
      }
      // Update cache with the freshly-saved version
      if (saved?.id) dashboardCache.set(saved.id, saved);
      setDashboard(saved);
      baselineRef.current = serializeTabs(saved.tabs);
      setIsDirty(false);
    } catch (err) {
      setError(err.message || 'Failed to save dashboard');
    } finally {
      setSaving(false);
    }
  }, [dashboard]);

  const discardChanges = useCallback(() => {
    if (baselineRef.current !== null) {
      setDashboard((prev) =>
        prev ? { ...prev, tabs: JSON.parse(baselineRef.current) } : prev
      );
      setIsDirty(false);
    }
  }, []);

  const createEmpty = useCallback(
    (name, tabs) => {
      const defaultTabs = tabs || [{ id: uuidv4(), label: 'Overview', widgets: [] }];
      const empty = { name: name || 'New Dashboard', tabs: defaultTabs };
      setDashboard(empty);
      baselineRef.current = serializeTabs(defaultTabs);
      setIsDirty(false);
    },
    []
  );

  return {
    dashboard,
    loading,
    saving,
    error,
    isDirty,
    updateTabWidgets,
    addTab,
    removeTab,
    renameTab,
    updateName,
    saveDashboard,
    discardChanges,
    createEmpty,
    refreshDashboard: () => fetchDashboard(true),
  };
}
